import React, { useRef, useState } from 'react';
import { UploadCloud, X, RefreshCw, Image as ImageIcon, Link2 } from 'lucide-react';
import api, { apiErrorMessage, mediaUrl } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

// Client-side guard mirroring the PHP /upload validation.
const ACCEPT = 'image/jpeg,image/png,image/webp';
const MAX_BYTES = 8 * 1024 * 1024;

// Uploads an image to the PHP backend (/api/upload) and stores the returned
// relative "/api/files/..." URL. Used by every image field in the CMS.
// Props:
//   value      current image URL ('' when none)
//   onChange   (url) => void   — called with the new URL after upload / paste
//   onRemove   () => void      — optional; called instead of onChange('') when
//                                the admin removes the image (lets the parent
//                                also delete the file and manage gallery slots)
//   compact    smaller preview (used for gallery thumbnails)
//   hint       small helper text under the empty dropzone
export default function ImageUpload({ value, onChange, onRemove, compact = false, hint }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const { toast } = useToast();

  const handleFile = async (file) => {
    if (!file) return;
    if (!ACCEPT.split(',').includes(file.type)) {
      toast({ title: 'Unsupported file type', description: 'Please choose a JPG, PNG or WebP image.', variant: 'destructive' });
      return;
    }
    if (file.size > MAX_BYTES) {
      toast({ title: 'Image too large', description: 'Maximum size is 8 MB.', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(`${BACKEND}${data.url}`);
    } catch (err) {
      toast({ title: 'Upload failed', description: apiErrorMessage(err), variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const pickFile = () => inputRef.current?.click();
  const remove = () => (onRemove ? onRemove() : onChange(''));

  const previewH = compact ? 'h-28' : 'h-40';

  return (
    <div>
      {value ? (
        <div className={`relative rounded-xl overflow-hidden border border-border group ${previewH}`}>
          <img src={mediaUrl(value)} alt="preview" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <button type="button" onClick={pickFile} disabled={uploading} data-testid="image-change-button"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-white text-black text-xs font-semibold disabled:opacity-60">
              <RefreshCw className={`w-3.5 h-3.5 ${uploading ? 'animate-spin' : ''}`} /> {uploading ? 'Uploading…' : 'Change'}
            </button>
            <button type="button" onClick={remove} data-testid="image-remove-button"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-destructive text-white text-xs font-semibold">
              <X className="w-3.5 h-3.5" /> Remove
            </button>
          </div>
        </div>
      ) : showUrl ? (
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (urlInput.trim()) { onChange(urlInput.trim()); setShowUrl(false); setUrlInput(''); } }}>
          <input type="url" autoFocus placeholder="Paste image URL and press Apply" value={urlInput} onChange={(e) => setUrlInput(e.target.value)}
            className="flex-1 h-11 rounded-md bg-secondary border border-border px-3 text-sm" data-testid="image-url-input" />
          <button type="submit" className="h-11 px-3 rounded-md bg-primary text-white text-sm font-semibold" data-testid="image-url-apply">Apply</button>
          <button type="button" onClick={() => { setShowUrl(false); setUrlInput(''); }} className="h-11 px-3 rounded-md bg-secondary border border-border text-sm">Upload</button>
        </form>
      ) : (
        <div>
          <button type="button" onClick={pickFile} disabled={uploading} data-testid="image-upload-button"
            className={`w-full ${previewH} rounded-xl border-2 border-dashed border-border grid place-items-center text-muted-foreground hover:border-primary hover:text-primary transition-colors bg-secondary/40`}>
            <div className="text-center">
              {uploading ? (
                <div className="flex items-center gap-2 justify-center"><ImageIcon className="w-4 h-4 animate-pulse" /> Uploading…</div>
              ) : (
                <><UploadCloud className="w-7 h-7 mx-auto mb-1.5" /><span className="font-heading font-semibold text-sm">Click to upload</span><p className="text-xs mt-1">{hint || 'JPG, PNG or WebP · up to 8 MB'}</p></>
              )}
            </div>
          </button>
          <button type="button" onClick={() => setShowUrl(true)} className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary"><Link2 className="w-3.5 h-3.5" /> or paste an image URL</button>
        </div>
      )}
      <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
    </div>
  );
}
